import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaVotingProgressModal.module.css";
import { IVotingBasis, QuorumType } from "src/stores/assemblies/VotingStore";

const AgendaVotingInformationTitle = observer(function AgendaVotingInformationTitle() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    return (
        <UI.Input
            label={`${t("labels.title")} *`}
            onChange={updateVotingFields("title")}
            value={votingStore.votingBasis.title}
        />
    );
});

const AgendaVotingInformationDropdown = observer(function AgendaVotingInformationDropdown() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    const handleQuorumSelect = useCallback(
        (value: string) => {
            votingStore.updateVotingBasisField("quorum", parseInt(value, 10));

            if (
                votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY &&
                votingStore.keys.length > 0 &&
                votingStore.votingBasis.key2 === ""
            ) {
                votingStore.updateVotingBasisField("key2", votingStore.keys[0].keyid);
            }
        },
        [votingStore]
    );

    return (
        <div className={styles.QuorumVetoRow}>
            <div className={styles.QuorumDiv}>
                <label className={styles.Label}>{`${t("screens.assemblies.vote.quorum")} *`}</label>
                <UI.Dropdown
                    id="quorum-select"
                    onSelect={handleQuorumSelect}
                    items={votingStore.quorums.map((item) => ({
                        label: item.label,
                        value: item.key.toString()
                    }))}
                    label={votingStore.quorums.find((q) => q.key === votingStore.votingBasis.quorum)?.label ?? ""}
                    className="mb-2"
                />
            </div>
            <UI.Checkbox
                name="veto"
                label={t("screens.assemblies.vote.veto")}
                checked={votingStore.votingBasis.veto}
                onChange={updateVotingFields("veto")}
                className={styles.Veto}
            />
        </div>
    );
});

const AgendaVotingInformationQuestion = observer(function AgendaVotingInformationQuestion() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    const updateVotingFields =
        <K extends keyof IVotingBasis>(field: K) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value =
                typeof votingStore.votingBasis[field] === "boolean"
                    ? (e.target as HTMLInputElement).checked
                    : e.target.value;

            votingStore.updateVotingBasisField(field, value as IVotingBasis[K]);
        };

    return (
        <UI.Input
            label={`${t("labels.question")} *`}
            as="textarea"
            onChange={updateVotingFields("question")}
            value={votingStore.votingBasis.question}
        />
    );
});

const AgendaVotingInformationVotingKeys = observer(function AgendaVotingInformationVotingKeys() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    const handleKey1Select = useCallback(
        (value: string) => {
            votingStore.updateVotingBasisField("key1", value);
        },
        [votingStore]
    );

    const handleKey2Select = useCallback(
        (value: string) => {
            votingStore.updateVotingBasisField("key2", value);
        },
        [votingStore]
    );

    return (
        <>
            <div className={styles.KeyDiv}>
                <label className={styles.Label}>{`${t("screens.assemblies.vote.voting_keys")} *`}</label>
                <UI.Dropdown
                    id="key-select"
                    onSelect={handleKey1Select}
                    items={votingStore.keys.map((item) => ({
                        label: item.name,
                        value: item.keyid
                    }))}
                    label={votingStore.keys.find((k) => k.keyid === votingStore.votingBasis.key1)?.name ?? ""}
                    className="mb-2"
                />
            </div>
            {votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY ? (
                <div className={styles.KeyDiv}>
                    <UI.Dropdown
                        id="key-select"
                        onSelect={handleKey2Select}
                        items={votingStore.keys.map((item) => ({
                            label: item.name,
                            value: item.keyid
                        }))}
                        label={votingStore.keys.find((k) => k.keyid === votingStore.votingBasis.key2)?.name ?? ""}
                        className="mb-2"
                    />
                </div>
            ) : undefined}
        </>
    );
});

export const AgendaVotingInformation = observer(function AgendaVotingInformation() {
    const { votingStore } = useContext(RootStoreContext);
    const { keys, votingBasis, updateVotingBasisField, isNewVoting, votingList, selectedVotingId } = votingStore;

    useEffect(() => {
        if (keys.length > 0 && votingBasis.key1 === "") {
            updateVotingBasisField("key1", keys[0].keyid);
        }
    }, [keys, votingBasis.key1, votingBasis.key2, updateVotingBasisField]);

    useEffect(() => {
        if (!isNewVoting) {
            const existingVoting = votingList.find((voting) => voting.votingid === selectedVotingId);
            if (existingVoting) {
                updateVotingBasisField("title", existingVoting.title);
                updateVotingBasisField("question", existingVoting.question);
            }
        }
    }, [isNewVoting, selectedVotingId, votingList, updateVotingBasisField]);

    return (
        <UI.Col>
            <AgendaVotingInformationTitle />
            <AgendaVotingInformationQuestion />
            <AgendaVotingInformationDropdown />
            <AgendaVotingInformationVotingKeys />
        </UI.Col>
    );
});
